import useLocale from 'next-translate/useTranslation';
import React, { useMemo, useState } from 'react';

import TemplateCategoryFilter from '@/components/media/TemplateCategoryFilter';
import TemplateMasonry from '@/components/media/TemplateMasonry';

import {
  getMediaTemplateCategories,
  getMediaTemplatesByKind,
  MediaTemplateKind,
} from '@/constants/mediaTemplates';

const VISIBLE_COUNT = 9;

interface DarkTemplatesGridProps {
  kind: MediaTemplateKind;
  basePath: string;
  // Already-translated strings — callers pass t() results from their own
  // namespace so this component works on any route.
  heading: string;
  subheading: string;
  showAllLabel: string;
}

// Dark, capped template catalogue for the cinematic landing pages. All
// template links stay in the DOM (SEO) — extras are hidden until "Show all".
const DarkTemplatesGrid = ({
  kind,
  basePath,
  heading,
  subheading,
  showAllLabel,
}: DarkTemplatesGridProps) => {
  const { t } = useLocale('media-templates');
  const [showAll, setShowAll] = useState(false);
  const [category, setCategory] = useState<string | null>(null);
  const templates = useMemo(
    () =>
      getMediaTemplatesByKind(kind).filter(
        (template) => template.slug && !template.skipOnLandingPage,
      ),
    [kind],
  );
  const categories = useMemo(
    () => getMediaTemplateCategories(templates),
    [templates],
  );

  return (
    <section className='relative py-20 lg:py-28'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-12 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {heading}
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-400'>
            {subheading}
          </p>
        </div>

        <TemplateCategoryFilter
          categories={categories}
          value={category}
          onChange={setCategory}
          total={templates.length}
          variant='dark'
          className='mb-8 sm:flex-wrap sm:justify-center sm:overflow-visible'
        />

        <TemplateMasonry
          templates={templates}
          columnsClassName='columns-2 lg:columns-3'
          getHref={(template) => `${basePath}/${template.slug}`}
          getText={(template) => ({
            title: t(template.title),
            description: t(template.description),
          })}
          ctaLabel={t('Use template')}
          // Every card stays in the DOM so the catalogue is crawlable. A picked
          // category shows every match; "All" keeps the cap.
          isHidden={(template, index) =>
            category
              ? template.category !== category
              : !showAll && index >= VISIBLE_COUNT
          }
        />

        {!showAll && !category && templates.length > VISIBLE_COUNT && (
          <div className='mt-10 text-center'>
            <button
              onClick={() => setShowAll(true)}
              className='rounded-full border border-white/15 bg-white/5 px-8 py-3 font-semibold text-white transition-all hover:border-white/40 hover:bg-white/10'
            >
              {showAllLabel} ({templates.length})
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default DarkTemplatesGrid;
